import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CameraCapture from './CameraCapture';
import LeadDashboard from './LeadDashboard';
import NavBar from './NavBar';
import { compressImage } from '../utils/imageCompression';
import { createThumbnail } from '../utils/createThumbnail';
import { imageToBase64, base64ToDataUrl } from '../utils/imageToBase64';
import { analyzeBusinessCardWithGPT } from '../services/gptVisionService';
import { saveLeadToFirestore } from '../services/firebaseService';
import { getUserCustomEvents, addUserCustomEvent } from '../services/userService';
import { canUserScan, incrementScanCount } from '../services/subscriptionService';
import Icon from './Icon';

function CESCardScanner() {
  const { user } = useAuth();
  const [capturedImage, setCapturedImage] = useState(null);
  const [leadData, setLeadData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [selectedConvention, setSelectedConvention] = useState('CES 2026');
  const [customConvention, setCustomConvention] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customEvents, setCustomEvents] = useState([]);
  const [scanLimit, setScanLimit] = useState(null);

  // Load custom events and scan limit from Firebase on mount
  useEffect(() => {
    const loadData = async () => {
      if (user?.uid) {
        try {
          const [events, limitCheck] = await Promise.all([
            getUserCustomEvents(user.uid),
            canUserScan(user.uid)
          ]);
          setCustomEvents(events);
          setScanLimit(limitCheck);
        } catch (error) {
          console.warn('Failed to load data:', error);
        }
      }
    };
    
    loadData();
  }, [user]);


  const handleImageCapture = async (frontFile, backFile = null) => {
    if (!frontFile) return;

    // Check scan limit before processing
    if (user?.uid) {
      try {
        const scanCheck = await canUserScan(user.uid);
        if (!scanCheck.canScan) {
          setError(`You've reached your scan limit (${scanCheck.limit} scans). Please upgrade to Pro for unlimited scans.`);
          return;
        }
        // Update scan limit state
        setScanLimit(scanCheck);
      } catch (error) {
        console.error('Error checking scan limit:', error);
        // Continue anyway if check fails
      }
    }

    setCapturedImage(frontFile);
    setError(null);
    setLeadData(null);
    
    // Create immediate preview URL for display during processing
    const immediatePreviewUrl = URL.createObjectURL(frontFile);
    setImageUrl(immediatePreviewUrl);
    
    setIsProcessing(true);

    let finalImageUrl = null;
    let leadId = null;

    try {
      // Step 1: Compress images for GPT-4o analysis (needs reasonable quality for OCR)
      const compressedFrontFile = await compressImage(frontFile);
      const compressedBackFile = backFile ? await compressImage(backFile) : null;
      
      // Step 2: Create thumbnails for display and storage
      const thumbnailFrontFile = await createThumbnail(frontFile, 400, 400, 0.7);
      const thumbnailBackFile = backFile ? await createThumbnail(backFile, 400, 400, 0.7) : null;
      
      // Step 3: Convert thumbnails to base64 for Firestore storage (no CORS needed!)
      const thumbnailFrontBase64 = await imageToBase64(thumbnailFrontFile);
      const thumbnailBackBase64 = thumbnailBackFile ? await imageToBase64(thumbnailBackFile) : null;
      
      // Step 4: Create data URL for final display (replace immediate preview)
      finalImageUrl = base64ToDataUrl(thumbnailFrontBase64);
      setImageUrl(finalImageUrl);
      
      // Clean up the immediate preview URL
      URL.revokeObjectURL(immediatePreviewUrl);
      
      // Step 5: Analyze with GPT-4o Vision (this is the main operation)
      const conventionName = showCustomInput && customConvention ? customConvention : selectedConvention;
      const analysis = await analyzeBusinessCardWithGPT(compressedFrontFile, conventionName, compressedBackFile);
      
      // Step 6: Save to Firestore with thumbnails as base64 (user-specific, no CORS!)
      try {
        if (user?.uid) {
          const leadDataToSave = {
            thumbnailBase64: thumbnailFrontBase64, // Store front thumbnail in Firestore as base64
            convention: conventionName, // Store convention name
            ...analysis,
          };
          
          // Add back thumbnail if exists
          if (thumbnailBackBase64) {
            leadDataToSave.thumbnailBackBase64 = thumbnailBackBase64;
          }
          
          leadId = await saveLeadToFirestore(leadDataToSave, user.uid);
        }
      } catch (firestoreError) {
        console.warn('Save to Firestore failed (non-critical):', firestoreError.message);
        // Continue without Firestore ID
      }
      
      // Show results immediately
      const leadDataResult = {
        id: leadId || 'local',
        convention: conventionName, // Include convention in lead data
        ...analysis,
        imageUrl: finalImageUrl,
        thumbnailBase64: thumbnailFrontBase64, // Keep base64 for potential re-display
      };
      
      // Add back thumbnail if exists
      if (thumbnailBackBase64) {
        leadDataResult.thumbnailBackBase64 = thumbnailBackBase64;
      }
      
      setLeadData(leadDataResult);
      
      // Increment scan count after successful scan
      if (user?.uid) {
        try {
          await incrementScanCount(user.uid);
          // Refresh scan limit to show updated count
          const updatedScanCheck = await canUserScan(user.uid);
          setScanLimit(updatedScanCheck);
        } catch (error) {
          console.warn('Failed to increment scan count:', error);
          // Non-critical, don't block the user
        }
      }
    } catch (err) {
      console.error('Processing error:', err);
      setError(err.message || 'Failed to process business card. Please try again.');
      // Clean up preview URL on error
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
      setImageUrl(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNewScan = async () => {
    // Clean up object URLs to prevent memory leaks
    if (imageUrl && imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imageUrl);
    }
    setCapturedImage(null);
    setLeadData(null);
    setImageUrl(null);
    setError(null);
    
    // Refresh scan limit after completing a scan
    if (user?.uid) {
      try {
        const scanCheck = await canUserScan(user.uid);
        setScanLimit(scanCheck);
      } catch (error) {
        console.warn('Failed to refresh scan limit:', error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 transition-colors duration-300">
      {/* Header */}
      {/* Navigation */}
      <NavBar />

      {/* Main Content */}
              <main className="max-w-md mx-auto px-4 pb-8">
                {!leadData ? (
                  <>
                    {/* Scan Limit Warning */}
                    {scanLimit && !scanLimit.canScan && (
                      <div className="mt-6 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/30 dark:to-orange-900/30 border border-red-400 dark:border-red-800 rounded-2xl p-4">
                        <div className="flex items-start gap-3">
                          <Icon emoji="⚠️" size="text-2xl" />
                          <div className="flex-1">
                            <h3 className="font-semibold text-red-700 dark:text-red-300 mb-1">Scan Limit Reached</h3>
                            <p className="text-sm text-red-600 dark:text-red-200 mb-3">
                              You've used all {scanLimit.limit} free scans. Upgrade to Pro for unlimited scans!
                            </p>
                            <button
                              onClick={() => window.location.href = '/subscription'}
                              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-semibold text-sm transition-all"
                            >
                              Upgrade to Pro
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Scan Count Display (Free Plan) */}
                    {scanLimit && scanLimit.canScan && scanLimit.limit !== Infinity && (
                      <div className="mt-6 bg-zinc-50 dark:bg-zinc-900 rounded-2xl p-4 border border-zinc-200 dark:border-zinc-800">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-zinc-600 dark:text-zinc-400">Scans Remaining</span>
                          <span className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
                            {scanLimit.remaining} / {scanLimit.limit}
                          </span>
                        </div>
                        <div className="w-full bg-zinc-200 dark:bg-zinc-800 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all"
                            style={{
                              width: `${Math.min(100, ((scanLimit.limit - scanLimit.remaining) / scanLimit.limit) * 100)}%`
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Convention Selector */}
                    <div className="mt-6 bg-zinc-50 dark:bg-zinc-900 rounded-2xl p-4 space-y-3 border border-zinc-200 dark:border-zinc-800">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-400">
                Convention/Event
              </label>
              {!showCustomInput ? (
                <div className="flex gap-2">
                  <select
                    value={selectedConvention}
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        setShowCustomInput(true);
                      } else {
                        setSelectedConvention(e.target.value);
                      }
                    }}
                    className="flex-1 px-4 py-2.5 bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600"
                  >
                    <option value="CES 2026">CES 2026</option>
                    <option value="MWC 2026">MWC 2026</option>
                    <option value="IFA 2026">IFA 2026</option>
                    <option value="TechCrunch Disrupt">TechCrunch Disrupt</option>
                    <option value="Web Summit">Web Summit</option>
                    {customEvents.length > 0 && (
                      <>
                        <option disabled>──────────</option>
                        {customEvents.map((event, index) => (
                          <option key={index} value={event}>{event}</option>
                        ))}
                      </>
                    )}
                    <option value="custom">+ Add Custom Event</option>
                  </select>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customConvention}
                    onChange={(e) => setCustomConvention(e.target.value)}
                    placeholder="Enter event name (e.g., SXSW 2026)"
                    className="flex-1 px-4 py-2.5 bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600"
                  />
                  <button
                    onClick={async () => {
                      const trimmedEvent = customConvention.trim();
                      if (trimmedEvent && user?.uid) {
                        setSelectedConvention(trimmedEvent);
                        // Add to custom events list in Firebase if not already there
                        if (!customEvents.includes(trimmedEvent)) {
                          try {
                            const updatedEvents = await addUserCustomEvent(user.uid, trimmedEvent);
                            setCustomEvents(updatedEvents);
                          } catch (error) {
                            console.error('Failed to save custom event:', error);
                            // Still add to local state even if Firebase fails
                            setCustomEvents([...customEvents, trimmedEvent]);
                          }
                        }
                        setShowCustomInput(false);
                        setCustomConvention('');
                      } else {
                        setShowCustomInput(false);
                      }
                    }}
                    className="px-4 py-2.5 bg-blue-600 dark:bg-zinc-700 hover:bg-blue-700 dark:hover:bg-zinc-600 text-white dark:text-zinc-100 rounded-lg transition-colors"
                  >
                    <Icon emoji="✓" size="text-base" />
                  </button>
                  <button
                    onClick={() => {
                      setShowCustomInput(false);
                      setCustomConvention('');
                    }}
                    className="px-4 py-2.5 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg transition-colors"
                  >
                    ✕
                  </button>
                </div>
              )}
              {!showCustomInput && (
                <p className="text-xs text-zinc-500 dark:text-zinc-500">
                  Selected: <span className="text-zinc-600 dark:text-zinc-400 font-medium">{selectedConvention}</span>
                </p>
              )}
            </div>
            
            {/* Show captured image during processing */}
            {isProcessing && capturedImage && imageUrl && (
              <div className="mt-6 bg-zinc-50 dark:bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800">
                <div className="bg-zinc-100 dark:bg-zinc-800 px-4 py-2 flex items-center justify-between">
                  <p className="text-xs text-zinc-600 dark:text-zinc-400">Processing...</p>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-zinc-400 dark:border-zinc-600 border-t-zinc-600 dark:border-t-zinc-300"></div>
                </div>
                <img
                  src={imageUrl}
                  alt="Business card being processed"
                  className="w-full h-auto"
                />
              </div>
            )}
            
            <CameraCapture
              onCapture={handleImageCapture}
              isProcessing={isProcessing}
              error={error}
              capturedImage={capturedImage}
            />
          </>
        ) : (
          <LeadDashboard
            leadData={leadData}
            imageUrl={imageUrl}
            onNewScan={handleNewScan}
            onUpdate={(updatedData) => setLeadData({ ...leadData, ...updatedData })}
          />
        )}
      </main>
    </div>
  );
}

export default CESCardScanner;

